import { SELECTED_PROJECT } from "@configuration";
import { ErrorBoundary } from "@features/common/boundaries/ErrorBoundary";
import ErrorFallback from "@features/common/ui/ErrorFallback";
import { PageLoader } from "@features/common/ui/PageLoader";
import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import PrivateRoute from "./auth/ProtectedRoute";

// COMMON
const ProductPage = lazy(() =>
  import("./features/products/ProductPage").then((module) => ({
    default: module.ProductPage,
  }))
);

// ALICIA
const AliciaViewAll = lazy(() =>
  import("./features/products/alicia/ViewAll").then((module) => ({
    default: module.AliciaViewAll,
  }))
);
const AliciaViewMine = lazy(() =>
  import("./features/products/alicia/ViewMine").then((module) => ({
    default: module.AliciaViewMine,
  }))
);
const ProductDetails = lazy(() =>
  import("./features/products/alicia/ProductDetails").then((module) => ({
    default: module.ProductDetails,
  }))
);
const ProductRequests = lazy(() =>
  import("./features/products/alicia/ProductRequests").then((module) => ({
    default: module.ProductRequests,
  }))
);
const ProductRequest = lazy(
  () => import("./features/products/alicia/ProductRequest")
);
const RequestStatus = lazy(
  () => import("./features/products/alicia/ProductRequests/RequestStatus")
);
const ProductRequestForProductionStatus = lazy(
  () =>
    import(
      "./features/products/alicia/ProductRequest/ProductRequestForProductionStatus"
    )
);
const DisclaimerPage = lazy(
  () =>
    import(
      "./features/products/alicia/ProductRequest/ProductionPhaseSupportRequest/DisclaimerPage"
    )
);
const ProductionPhaseView = lazy(
  () => import("./features/products/alicia/ProductRequest/ProductionPhaseView")
);

// ALLIANCE
const AllianceViewAll = lazy(() =>
  import("./features/products/alliance/ViewAll").then((module) => ({
    default: module.ProductsListDatasets,
  }))
);
const AllianceViewMine = lazy(() =>
  import("./features/products/alliance/ViewMine").then((module) => ({
    default: module.AllianceViewMine,
  }))
);
const DatasetDetails = lazy(() =>
  import("./features/products/alliance/DatasetDetails").then((module) => ({
    default: module.DatasetDetails,
  }))
);
const SystemicInnovations = lazy(() =>
  import("./features/systemicInnovation/ViewAll").then((module) => ({
    default: module.SystemicInnovations,
  }))
);
const MySystemicInnovations = lazy(() =>
  import("./features/systemicInnovation/ViewMine").then((module) => ({
    default: module.MySystemicInnovations,
  }))
);
const SystemicInnovationDetails = lazy(
  () => import("./features/systemicInnovation/SystemicInnovationDetails")
);
const SystemicInnovationPage = lazy(() =>
  import("./features/systemicInnovation/CreateEditSystemicInnovation").then(
    (module) => ({ default: module.SystemicInnovation })
  )
);

// R3-MYDAS
const DigitalProductPassport = lazy(
  () => import("./features/digitalProductPassport/ViewDppDetails")
);
const ViewBatteryPassportIframe = lazy(
  () => import("./features/digitalProductPassport/ViewBatteryPassportIframe")
);

// Old
const PromoPage = lazy(() => import("./features/PromoPage"));
const CompleteRegistration = lazy(
  () => import("./features/CompleteRegistration")
);
const LandingPage = lazy(() => import("./features/LandingPage"));
const Company = lazy(() => import("./features/Company"));

const Cart = lazy(() =>
  import("./features/Cart").then((module) => ({
    default: module.Cart,
  }))
);
const Checkout = lazy(() =>
  import("./features/Cart/Checkout").then((module) => ({
    default: module.Checkout,
  }))
);
const MyProfile = lazy(() => import("./features/MyProfile"));

const BackOffice = lazy(() => import("./features/BackOffice"));
const About = lazy(() => import("./features/About"));
const NotFoundPage = lazy(() => import("./features/NotFoundPage"));

export function RoutesConfig() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* COMMON */}
          <Route path="/home" element={<PromoPage />} />
          <Route
            path="/complete-registration"
            element={<CompleteRegistration />}
          />
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/company"
            element={<PrivateRoute element={<Company />} />}
          />
          <Route
            path="/product"
            element={<PrivateRoute element={<ProductPage />} />}
          />

          {/* ALICIA */}
          {SELECTED_PROJECT === "alicia" && (
            <>
              <Route
                path="/products"
                element={<PrivateRoute element={<AliciaViewAll />} />}
              />
              <Route
                path="/edit-product/:id"
                element={<PrivateRoute element={<ProductPage />} />}
              />
              <Route
                path="/product-details/:id"
                element={<PrivateRoute element={<ProductDetails />} />}
              />
              <Route
                path={"/asset-management"}
                element={<PrivateRoute element={<AliciaViewMine />} />}
              />
              <Route
                path="/my-requests"
                element={<PrivateRoute element={<ProductRequests />} />}
              />
              <Route
                path="/product-request/:id"
                element={<PrivateRoute element={<ProductRequest />} />}
              />
              <Route
                path="/product-request-status/:id"
                element={<PrivateRoute element={<RequestStatus />} />}
              />
              <Route
                path="/product-request-for-production-status"
                element={
                  <PrivateRoute
                    element={<ProductRequestForProductionStatus />}
                  />
                }
              />
              <Route
                path="/production-phase-support-request-disclaimer"
                element={<PrivateRoute element={<DisclaimerPage />} />}
              />
              <Route
                path="/production-phase-view/:id"
                element={<PrivateRoute element={<ProductionPhaseView />} />}
              />
            </>
          )}

          {/* ALLIANCE */}
          {SELECTED_PROJECT === "alliance" && (
            <>
              <Route
                path="/dataset"
                element={<PrivateRoute element={<ProductPage />} />}
              />
              <Route
                path="/datasets"
                element={<PrivateRoute element={<AllianceViewAll />} />}
              />
              <Route
                path="/dataset-details/:id"
                element={<PrivateRoute element={<DatasetDetails />} />}
              />
              <Route
                path={"/my-datasets"}
                element={<PrivateRoute element={<AllianceViewMine />} />}
              />
              <Route
                path="/edit-dataset/:id"
                element={<PrivateRoute element={<ProductPage />} />}
              />
              <Route
                path="/systemic-innovations"
                element={<PrivateRoute element={<SystemicInnovations />} />}
              />
              <Route
                path="/my-systemic-innovations"
                element={<PrivateRoute element={<MySystemicInnovations />} />}
              />
              <Route
                path="/systemic-innovation-details/:id"
                element={
                  <PrivateRoute element={<SystemicInnovationDetails />} />
                }
              />
              <Route
                path="/systemic-innovation"
                element={<PrivateRoute element={<SystemicInnovationPage />} />}
              />
              <Route
                path="/edit-systemic-innovation/:id"
                element={<PrivateRoute element={<SystemicInnovationPage />} />}
              />
            </>
          )}

          {/* R3-Mydas */}
          {SELECTED_PROJECT === "r3-mydas" && (
            <>
              <Route
                path="/products"
                element={<PrivateRoute element={<AliciaViewAll />} />}
              />
              <Route
                path="/edit-product/:id"
                element={<PrivateRoute element={<ProductPage />} />}
              />
              <Route
                path="/product-details/:id"
                element={<PrivateRoute element={<ProductDetails />} />}
              />
              <Route
                path={"/asset-management"}
                element={<PrivateRoute element={<AliciaViewMine />} />}
              />
              <Route
                path="/digital-product-passport/:id?"
                element={<PrivateRoute element={<DigitalProductPassport />} />}
              />
              <Route
                path="/battery-passport"
                element={
                  <PrivateRoute element={<ViewBatteryPassportIframe />} />
                }
              />
            </>
          )}

          {/* Old */}

          <Route path="/cart" element={<PrivateRoute element={<Cart />} />} />
          <Route
            path="/checkout"
            element={<PrivateRoute element={<Checkout />} />}
          />
          <Route
            path="/my-profile"
            element={<PrivateRoute element={<MyProfile />} />}
          />

          <Route
            path="/back-office"
            element={<PrivateRoute element={<BackOffice />} />}
          />
          <Route path="/about" element={<PrivateRoute element={<About />} />} />
          <Route
            path="/*"
            element={<PrivateRoute element={<NotFoundPage />} />}
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
